import React from 'react';

import { formatPercentCell, TimeReportDaysCell, TimeReportMoneyCell } from './timeReportFormat';

import { formatFigure } from 'Helpers/formatService';
import { MARGIN_NO_DIVISOR } from 'Helpers/marginPercentage';

import Badge from 'CommonComponents/Badge';

/**
 * A group's day-weighted average rate. The server sends null when the group has
 * no days to divide by, which reads as the dash rather than a blank cell.
 */
function RateCell({ value, currency }) {
    if (value === null || value === undefined) {
        return MARGIN_NO_DIVISOR;
    }

    return <TimeReportMoneyCell value={value} currency={currency} />;
}

function TimeReportRow({ row, currency, onDrillThrough }) {
    return (
        <tr className="tw-cursor-pointer" onClick={() => onDrillThrough(row)}>
            <td className="tw-text-start">
                {/* The button is the keyboard path to the panel; the row's own
                    handler is the mouse one, and stopping propagation here keeps
                    a click on the button from opening it twice. */}
                <button
                    type="button"
                    className="tw-font-semibold tw-text-blue hover:tw-text-blue-75"
                    onClick={(event) => {
                        event.stopPropagation();
                        onDrillThrough(row);
                    }}
                >
                    {row.label}
                </button>
                {row.freelance && (
                    <Badge variant="info" className="tw-ms-2">
                        {$t('Freelance')}
                    </Badge>
                )}
            </td>
            <td className="inbud-numeric-cell">{formatFigure(row.people, String)}</td>
            <td className="inbud-numeric-cell">
                <TimeReportDaysCell value={row.days} />
            </td>
            <td className="inbud-numeric-cell">
                <RateCell value={row.cost_rate} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">
                <TimeReportMoneyCell value={row.time_cost} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">
                <RateCell value={row.billed_rate} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">
                <TimeReportMoneyCell value={row.billed_value} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">
                <TimeReportMoneyCell value={row.margin} currency={currency} />
            </td>
            <td className="inbud-numeric-cell">{formatPercentCell(row.margin_percent, 1)}</td>
            <td className="inbud-numeric-cell">{formatPercentCell(row.markup_percent, 1)}</td>
        </tr>
    );
}

/**
 * The total row is whatever the server sent, never a sum of the rows above it.
 * People is a distinct headcount, so somebody who worked two roles appears in
 * two rows and once in the total — adding the column would double-count them.
 */
export default function TimeReportTable({ groupLabel, rows, total, currency, onDrillThrough }) {
    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th className="tw-text-start">{groupLabel}</th>
                        <th className="tw-text-end">{$t('People')}</th>
                        <th className="tw-text-end">{$t('Days')}</th>
                        <th className="tw-text-end">{$t('Cost rate')}</th>
                        <th className="tw-text-end">Cost to date</th>
                        <th className="tw-text-end">Charge rate</th>
                        <th className="tw-text-end">Charged</th>
                        <th className="tw-text-end">{$t('Margin')}</th>
                        <th className="tw-text-end">{$t('Margin %')}</th>
                        <th className="tw-text-end">{$t('Markup %')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={10} className="tw-text-center tw-text-charcoal-75">
                                {$t('No time recorded on this project inside the selected filters.')}
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => (
                        <TimeReportRow key={row.group_key} row={row} currency={currency} onDrillThrough={onDrillThrough} />
                    ))}
                </tbody>
                {total && (
                    <tfoot>
                        <tr>
                            <td className="tw-text-start">{$t('Total')}</td>
                            <td className="inbud-numeric-cell">{formatFigure(total.people, String)}</td>
                            <td className="inbud-numeric-cell">
                                <TimeReportDaysCell value={total.days} />
                            </td>
                            {/* No single rate describes a whole project, so both
                                rate columns are left empty rather than filled. */}
                            <td />
                            <td className="inbud-numeric-cell">
                                <TimeReportMoneyCell value={total.time_cost} currency={currency} />
                            </td>
                            <td />
                            <td className="inbud-numeric-cell">
                                <TimeReportMoneyCell value={total.billed_value} currency={currency} />
                            </td>
                            <td className="inbud-numeric-cell">
                                <TimeReportMoneyCell value={total.margin} currency={currency} />
                            </td>
                            <td className="inbud-numeric-cell">{formatPercentCell(total.margin_percent, 1)}</td>
                            <td className="inbud-numeric-cell">{formatPercentCell(total.markup_percent, 1)}</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
